const encoder = new TextEncoder();
const decoder = new TextDecoder();

function bufToHex(buf) {
  const bytes = new Uint8Array(buf);
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

function hexToBuf(hex) {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytes;
}

/**
 * Derive raw 32-byte key using PBKDF2(SHA-512) so it matches Node's pbkdf2Sync(..., 32, 'sha512')
 */
async function deriveRawKey(password, salt, iterations = 65536) {
  const pwBuf = encoder.encode(password);
  const saltBuf = encoder.encode(salt);

  const baseKey = await crypto.subtle.importKey(
    'raw',
    pwBuf,
    'PBKDF2',
    false,
    ['deriveBits']
  );

  const derivedBits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: saltBuf, iterations, hash: 'SHA-512' },
    baseKey,
    256
  );

  return derivedBits; // ArrayBuffer (32 bytes)
}

/**
 * Encrypt plaintext (utf8) using AES-256-CBC with a random IV per call.
 * Output format: iv_hex (32 chars) + ciphertext_hex
 *
 * @param {string} plaintext
 * @param {string} password
 * @param {string} salt
 * @returns {Promise<string>} hex string: IV || ciphertext
 */
export async function encryptAes256CbcToHex(plaintext, password, salt) {
  const rawKey = await deriveRawKey(password, salt, 65536);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: 'AES-CBC' },
    false,
    ['encrypt']
  );

  // Fresh random IV every time — this is the key fix
  const iv = crypto.getRandomValues(new Uint8Array(16));

  const ctBuffer = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    cryptoKey,
    encoder.encode(plaintext)
  );

  return bufToHex(iv) + bufToHex(ctBuffer);
}

/**
 * Decrypt a hex string produced by encryptAes256CbcToHex (IV || ciphertext).
 */
export async function decryptAes256CbcFromHex(hexPayload, password, salt) {
  const ivHex = hexPayload.slice(0, 32); // 16 bytes = 32 hex chars
  const ctHex = hexPayload.slice(32);

  const iv = hexToBuf(ivHex);
  const rawKey = await deriveRawKey(password, salt, 65536);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: 'AES-CBC' },
    false,
    ['decrypt']
  );

  const ptBuffer = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv },
    cryptoKey,
    hexToBuf(ctHex)
  );

  return decoder.decode(ptBuffer);
}

/* Example usage:
(async () => {
  const plaintext = 'Hello secure world!';
  const password = config.req_enc_key;
  const salt = config.req_salt;
  const hex = await encryptAes256CbcToHex(plaintext, password, salt);
  console.log('payload hex (iv+ciphertext):', hex);
})();
*/